import type { SubmitClaimFormDetails } from '@assembly-web/services';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';
import { z } from 'zod';

import { InlineError } from '../../../DesignSystem/Feedback/InlineError';
import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import {
  RichTextEditor,
  type SerializedData,
} from '../Editors/RichTextEditor/RichTextEditor';

export type SubmitClaimData = {
  text: {
    plainText: string;
    messageTokens: string;
    messageHtml: string;
  };
  file?: unknown;
};

type SubmitChallengeFormProps = {
  onSubmit: (data: SubmitClaimData) => void;
  formMeta: SubmitClaimFormDetails;
};

const messages = defineMessages({
  openEndedBlockLabel: {
    defaultMessage: 'Describe what you did',
    id: 'M+6U7a',
  },
  fileUploadBlockLabel: {
    defaultMessage: 'Upload a screenshot of your review',
    id: 'WgQFiX',
  },
});

const errorMessages = defineMessages({
  fieldRequired: {
    defaultMessage: 'This field is required',
    id: 'TKmub+',
  },
  tooLong: {
    defaultMessage: 'Content should be less than 1000 characters',
    id: 'mhx8mR',
  },
  // can add more messages when file upload is added
});

type Inputs = {
  openEnded: string;
};

const getFormSchema = (
  formatMessage: (descriptor: MessageDescriptor) => string
) =>
  z.object({
    openEnded: z
      .string()
      .min(1, formatMessage(errorMessages.fieldRequired))
      .max(1000, 'Content should be less than 1000 characters'),
  });

export function SubmitChallengeForm(props: SubmitChallengeFormProps) {
  const { formatMessage } = useIntl();
  const { onSubmit, formMeta } = props;
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      openEnded: '',
    },
    resolver: zodResolver(getFormSchema(formatMessage)),
  });

  const [editorContent, setEditorContent] = useState<SerializedData>({
    html: '',
    plainText: '',
    json: '',
  });

  const submitHandler = () => {
    onSubmit({
      text: {
        messageHtml: editorContent.html,
        messageTokens: editorContent.json,
        plainText: editorContent.plainText,
      },
    });
  };

  const error = errors['openEnded']?.message;
  const { formType, textFieldLabel } = formMeta;

  return (
    <form
      id="challenge-submission-form"
      className="flex flex-col gap-4"
      onSubmit={handleSubmit(submitHandler)}
    >
      <section className="flex flex-col gap-2">
        {(formType === 'onlyOpenEnded' ||
          formType === 'fileUploadAndOpenEnded') && (
          <>
            <TextStyle variant="sm-medium">
              {textFieldLabel ?? formatMessage(messages.openEndedBlockLabel)}
            </TextStyle>
            <Controller
              control={control}
              name="openEnded"
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  onChange={(editorContent) => {
                    const { plainText } = editorContent;
                    setEditorContent(editorContent);
                    onChange(plainText);
                  }}
                  placeholder="Type here..."
                />
              )}
            />
          </>
        )}
        {Boolean(error) && (
          <InlineError id="openEnded-error" size="xs" as="span">
            {error}
          </InlineError>
        )}
      </section>
    </form>
  );
}
